import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { 
  ArrowLeft, 
  Send, 
  User, 
  Mail, 
  Phone, 
  Loader2,
  Info,
  Star
} from 'lucide-react';

import Header from '../../Components/Header';
import Footer from '../../Components/Footer';

const Interested = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const product = location.state?.product;

  const [formData, setFormData] = useState({
    customerName: '',
    customerEmail: '',
    customerPhone: '',
  });

  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    document.title = "Marvel Sato | Inquery";

    if (!product) {
      navigate('/products');
    }
  }, [product, navigate]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const payload = {
      productId: product._id,
      ...formData
    };

    try {
      // Endpoint matches your: app.use("/api/inquery") + router.post("/interested")
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/inquery/interested`, payload);
      
      if (response.status === 201) {
        setSubmitted(true);
        setTimeout(() => navigate('/products'), 3000);
      }
    } catch (error) {
      console.error("Inquiry failed:", error.response?.data || error.message);
      alert("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="h-screen flex items-center justify-center bg-slate-900 text-white">
        <motion.div 
          initial={{ opacity: 0, y: 20 }} 
          animate={{ opacity: 1, y: 0 }}
          className="text-center p-8"
        >
          <div className="w-20 h-20 bg-orange-500 text-white rounded-full flex items-center justify-center mx-auto mb-6 shadow-[0_0_30px_rgba(249,115,22,0.4)]">
            <Star size={40} fill="currentColor" />
          </div>
          <h2 className="text-4xl font-black uppercase italic tracking-tighter mb-4">Inquiry send Successfully</h2>
          <p className="text-slate-400 font-medium max-w-md mx-auto">
            We've noted your interest in the <span className="text-white font-bold">{product?.productName}</span>. 
            A specialist will reach out with technical details.
          </p> 
        </motion.div>
      </div>
    );
  }

  return (
    <div className="bg-white min-h-screen font-sans">
      <Header />
      
      <div className="max-w-5xl mx-auto px-6 pt-32 pb-20">
        <Link to="/products" className="inline-flex items-center gap-2 text-slate-400 hover:text-slate-900 font-black text-[10px] uppercase tracking-[0.2em] mb-12 transition-all">
          <ArrowLeft size={14} /> Back to Fleet
        </Link>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
          {/* Left Side: Minimal Product Info */}
          <div>
            <span className="bg-orange-500 text-white text-[9px] font-black px-3 py-1 rounded-full uppercase tracking-widest">
              Express Interest
            </span>
            <h1 className="text-5xl font-black text-slate-900 uppercase italic tracking-tighter mt-6 mb-4">
              Seeking more <span className="text-orange-500 not-italic">Info?</span>
            </h1>
            <p className="text-slate-500 text-sm leading-relaxed mb-8">
              Leave your details and our engineering team will provide you with a full technical breakdown and custom pricing for the <span className="font-bold text-slate-900">{product?.productName}</span>.
            </p>

            <div className="bg-slate-50 p-6 rounded-2xl border border-slate-100 flex items-center gap-5">
              <img src={product?.productImage1} alt="" className="w-24 h-20 object-cover rounded-lg shadow-md" />
              <div>
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Selected Model</p>
                <p className="text-lg font-black text-slate-900 uppercase italic">{product?.productName}</p>
              </div>
            </div>
          </div>

          {/* Right Side: Simple Form */}
          <div className="bg-white p-2 border-2 border-slate-900 rounded-[2rem]">
            <div className="bg-slate-900 rounded-[1.8rem] p-8 md:p-10">
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-1.5">
                  <label className="text-[9px] font-black uppercase text-slate-500 ml-1 tracking-widest">Full Name</label>
                  <div className="relative">
                    <User className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-600" size={16} />
                    <input 
                      required
                      name="customerName"
                      onChange={handleChange}
                      className="w-full bg-slate-800 border-none text-white rounded-xl py-4 pl-12 pr-4 focus:ring-2 focus:ring-orange-500 outline-none transition-all text-sm"
                      placeholder="Your Name"
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label className="text-[9px] font-black uppercase text-slate-500 ml-1 tracking-widest">Email Address</label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-600" size={16} />
                    <input 
                      required
                      type="email"
                      name="customerEmail"
                      onChange={handleChange}
                      className="w-full bg-slate-800 border-none text-white rounded-xl py-4 pl-12 pr-4 focus:ring-2 focus:ring-orange-500 outline-none transition-all text-sm"
                      placeholder="email@example.com"
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label className="text-[9px] font-black uppercase text-slate-500 ml-1 tracking-widest">Phone Number</label>
                  <div className="relative">
                    <Phone className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-600" size={16} />
                    <input 
                      required
                      type="tel"
                      name="customerPhone"
                      onChange={handleChange}
                      className="w-full bg-slate-800 border-none text-white rounded-xl py-4 pl-12 pr-4 focus:ring-2 focus:ring-orange-500 outline-none transition-all text-sm"
                      placeholder="+1 000 000 000"
                    />
                  </div>
                </div>

                <button 
                  disabled={loading}
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white py-5 rounded-xl font-black uppercase tracking-[0.2em] text-xs transition-all flex items-center justify-center gap-3 mt-4"
                >
                  {loading ? <Loader2 className="animate-spin" size={18} /> : (
                    <>Submit <Send size={16} /></>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Interested;